import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { pillars } from '../data/content';

gsap.registerPlugin(ScrollTrigger);

export default function PlatformPillars() {
  const headingRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Heading reveal
      gsap.fromTo(
        headingRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headingRef.current,
            start: 'top 85%',
          },
        }
      );

      // Cards stagger reveal
      const cards = cardsRef.current?.querySelectorAll('.platform-card');
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 50, opacity: 0, scale: 0.95 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <section id="platform" className="section-container py-16 sm:py-20" aria-labelledby="platform-title">
      <div ref={headingRef} className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end opacity-0">
        <div>
          <p className="text-sm font-extrabold uppercase tracking-[0.25em] text-violet-600">Core platform</p>
          <h2 id="platform-title" className="mt-3 max-w-3xl text-3xl font-extrabold tracking-[-0.04em] text-slate-950 sm:text-5xl">A connected intelligence layer for real life and real work.</h2>
        </div>
        <p className="max-w-md text-base leading-7 text-slate-600">Relate.ai moves beyond isolated conversations into a system that learns, connects, acts, and evolves with your world.</p>
      </div>
      <div ref={cardsRef} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {pillars.map(({ title, body, icon: Icon }) => (
          <article key={title} className="platform-card glass-card p-6 transition duration-300 hover:-translate-y-1 hover:bg-white/85 opacity-0">
            <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-violet-500 to-rose-500 text-white shadow-lg">
              <Icon size={22} aria-hidden="true" />
            </div>
            <h3 className="text-xl font-extrabold text-slate-950">{title}</h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
